import type { RaceSummary, ScheduleData } from "@/api";
import { trackTitle } from "@/features/racing/venue";

import { localDateTime, localTime, type ScheduleWindow } from "./scheduleState";
import {
  CALENDAR_EDGE,
  CALENDAR_PX_PER_MS,
  useCalendarScroll,
} from "./useCalendarScroll";

const TILE_WIDTH = 64;
const LANE_HEIGHT = 80;

export function calendarLanes(races: RaceSummary[], from: number) {
  const ends: number[] = [];
  const tiles = races.map((race) => {
    const left =
      CALENDAR_EDGE +
      (Date.parse(race.scheduledAt) - from) * CALENDAR_PX_PER_MS;
    const available = ends.findIndex((end) => end + 8 <= left);
    const lane = available === -1 ? ends.length : available;
    ends[lane] = left + TILE_WIDTH;
    return { race, left, lane };
  });
  return { tiles, height: Math.max(1, ends.length) * LANE_HEIGHT + 12 };
}

export function ScheduleCalendar({
  races,
  tracks,
  window,
  now,
  follow,
  focusNow,
  selected,
  detailsId,
  onSelect,
  onBrowse,
}: {
  races: RaceSummary[];
  tracks: ScheduleData["tracks"];
  window: ScheduleWindow;
  now: number;
  follow: boolean;
  focusNow: number;
  selected: string | null;
  detailsId: string;
  onSelect: (race: RaceSummary) => void;
  onBrowse: () => void;
}) {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  const width = Math.max(
    320,
    (to - from) * CALENDAR_PX_PER_MS + CALENDAR_EDGE * 2 + TILE_WIDTH,
  );
  const { scroll, onScroll } = useCalendarScroll({
    from,
    now,
    follow,
    focusNow,
    onBrowse,
  });
  const grouped = new Map(
    tracks.map((track) => [
      track.slug,
      { ...track, races: [] as RaceSummary[] },
    ]),
  );
  races.forEach((race) => {
    if (!grouped.has(race.track))
      grouped.set(race.track, {
        slug: race.track,
        name: trackTitle(race),
        races: [],
      });
    grouped.get(race.track)!.races.push(race);
  });
  const ticks: number[] = [];
  for (
    let tick = Math.ceil(from / 900_000) * 900_000;
    tick < to;
    tick += 900_000
  )
    ticks.push(tick);
  const marker = CALENDAR_EDGE + (now - from) * CALENDAR_PX_PER_MS;
  const showNow = now >= from && now < to;

  return (
    <div className="schedule-matrix">
      <div
        className="schedule-matrix__scroll"
        ref={scroll}
        onScroll={onScroll}
        tabIndex={0}
        role="region"
        aria-label="Race calendar, scroll horizontally to browse times"
      >
        <div
          className="schedule-matrix__canvas"
          style={{ width: `calc(${width}px + var(--schedule-label-width))` }}
        >
          <div className="schedule-matrix__times">
            <span className="schedule-matrix__corner">League / local time</span>
            <div>
              {ticks.map((tick) => (
                <time
                  key={tick}
                  dateTime={new Date(tick).toISOString()}
                  style={{
                    left: CALENDAR_EDGE + (tick - from) * CALENDAR_PX_PER_MS,
                  }}
                >
                  {localTime(new Date(tick).toISOString())}
                </time>
              ))}
              {showNow ? (
                <span
                  className="schedule-matrix__now-label"
                  style={{ left: marker }}
                >
                  Now
                </span>
              ) : null}
            </div>
          </div>
          {[...grouped.values()].map((track) => {
            const { tiles, height } = calendarLanes(track.races, from);
            return (
              <div
                className="schedule-track"
                key={track.slug}
                style={{ minHeight: height }}
              >
                <strong>
                  {track.name}
                  <small>{track.races.length} races</small>
                </strong>
                <div className="schedule-track__timeline">
                  {ticks.map((tick) => (
                    <span
                      className="schedule-matrix__tick"
                      key={tick}
                      style={{
                        left:
                          CALENDAR_EDGE + (tick - from) * CALENDAR_PX_PER_MS,
                      }}
                      aria-hidden="true"
                    />
                  ))}
                  {showNow ? (
                    <span
                      className="schedule-matrix__now"
                      style={{ left: marker }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {tiles.map(({ race, left, lane }) => (
                    <button
                      key={race.raceId}
                      type="button"
                      className={`schedule-race schedule-race--${race.status}`}
                      style={{ left, top: 10 + lane * LANE_HEIGHT }}
                      aria-label={`${track.name}, ${race.distanceM} metres, ${localDateTime(race.scheduledAt)}, ${race.status.replaceAll("_", " ")}${race.raceNumber ? `, ${race.raceNumber}` : ""}`}
                      aria-pressed={selected === race.raceId}
                      aria-expanded={selected === race.raceId}
                      aria-controls={detailsId}
                      onClick={() => onSelect(race)}
                    >
                      <b>{race.raceNumber ?? "Race"}</b>
                      <span>{race.distanceM}m</span>
                      <time dateTime={race.scheduledAt}>
                        {localTime(race.scheduledAt)}
                      </time>
                    </button>
                  ))}
                  {!tiles.length ? (
                    <span className="schedule-track__empty">
                      No matching races in this window
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="schedule-matrix__legend">
        <span>Scroll to browse · select a race for details</span>
        <span>
          <i /> Running <i /> Betting open
        </span>
      </div>
    </div>
  );
}
